/**
 * Wallet Monetization Components
 * 
 * A) StreakCard - Daily login streak with bonus psychology
 * B) BoostImpactPreview - CTR estimate for boost options
 * C) BoostSuggestionCard - "3x more with boost" AI suggestion
 * D) GrowthProgressBar - Reframed progress bar for coin accumulation
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Flame, TrendingUp, Sparkles, Zap, ChevronRight, AlertCircle, Info, Coins, Check } from 'lucide-react';
import { formatCoins } from '../lib/coinFormat';
import { buildShareUrl, shareNative, canNativeShare } from '../utils/shareUtils';
// =====================================================
// HELPERS
// =====================================================

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
function round(n) { return Math.round(n); }
function pct(n) { return `${round(n * 100)}%`; }

const BOOST_MULT = {
  highlight_24h: { viewsMult: 1.25, ctrMult: 1.10 },
  urgent_7d: { viewsMult: 1.10, ctrMult: 1.15 },
  auto_refresh_24h: { viewsMult: 1.35, ctrMult: 1.05 },
  top_category_24h: { viewsMult: 1.60, ctrMult: 1.08 },
  homepage_featured_24h: { viewsMult: 2.20, ctrMult: 1.12 },
  // Backend codes
  BOOST_HIGHLIGHT_24H: { viewsMult: 1.25, ctrMult: 1.10 },
  BADGE_URGENT_7D: { viewsMult: 1.10, ctrMult: 1.15 },
  AUTO_REFRESH_24H: { viewsMult: 1.35, ctrMult: 1.05 },
  BOOST_TOP_CATEGORY_24H: { viewsMult: 1.60, ctrMult: 1.08 },
  BOOST_HOMEPAGE_24H: { viewsMult: 2.20, ctrMult: 1.12 },
};

/**
 * Estimate boost impact using heuristics
 */
export function estimateBoostImpact({
  boostType,
  baseViews7d = 50,
  baseCtr = 0.018,
  categoryDemand = 1.0,
  cityDemand = 1.0,
  leadFactor = 0.22,
}) {
  const mult = BOOST_MULT[boostType] || { viewsMult: 1.2, ctrMult: 1.05 };
  const catDemand = clamp(categoryDemand, 0.8, 1.2);
  const ctyDemand = clamp(cityDemand, 0.8, 1.2);
  
  const estViews = baseViews7d * mult.viewsMult * catDemand * ctyDemand;
  const estCtr = baseCtr * mult.ctrMult;
  const estLeads = estViews * estCtr * leadFactor;
  
  const uplift = estViews / Math.max(1, baseViews7d);
  return {
    estViews: round(estViews),
    estCtr,
    estLeads: Math.max(0, round(estLeads)),
    uplift,
  };
}

/**
 * Calculate streak bonus based on days
 * day 1-2: +0, day 3-6: +1, day 7-13: +2, day 14-20: +3, day 21-30: +4
 */
export function calculateStreakBonus(streakDays) {
  if (streakDays < 3) return 0;
  if (streakDays < 7) return 1;
  if (streakDays < 14) return 2;
  if (streakDays < 21) return 3;
  return 4;
}

/**
 * Calculate days until next bonus tier
 */
export function daysUntilNextBonus(streakDays) {
  if (streakDays < 3) return 3 - streakDays;
  if (streakDays < 7) return 7 - streakDays;
  if (streakDays < 14) return 14 - streakDays;
  if (streakDays < 21) return 21 - streakDays;
  return 0; // At max tier
}

// =====================================================
// A) STREAK CARD
// =====================================================

export function StreakCard({
  lang = 'en',
  streakDays = 0,
  bonusToday = 0,
  isClaimedToday = false,
  onClaim,
  claiming = false,
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';
  
  const nextBonusInDays = daysUntilNextBonus(streakDays);
  const currentBonus = calculateStreakBonus(streakDays);
  
  const labels = {
    title: isArabic ? 'سلسلة المكافآت' : 'Streak Rewards',
    streak: isArabic ? `سلسلة: ${streakDays} يوم` : `Streak: ${streakDays} days`,
    bonus: bonusToday > 0 
      ? (isArabic ? `مكافأة اليوم: +${bonusToday}` : `Today's bonus: +${bonusToday}`)
      : (isArabic ? 'ابدأ سلسلتك!' : 'Start your streak!'),
    next: nextBonusInDays > 0
      ? (isArabic ? `المكافأة الجاية بعد ${nextBonusInDays} يوم` : `Next bonus in ${nextBonusInDays} days`)
      : (isArabic ? 'أنت في أعلى مستوى!' : 'You\'re at max tier!'),
    cta: isClaimedToday 
      ? (isArabic ? 'تم الاستلام' : 'Claimed') 
      : (isArabic ? 'استلم مكافأتك' : 'Claim Today\'s Coin'),
    comeback: isArabic ? 'ارجع بكرة' : 'Come back tomorrow',
  };
  
  return (
    <div 
      dir={dir}
      className="w-full rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50/50 p-4 shadow-sm"
      data-testid="streak-card"
    >
      <div className="flex items-start gap-3">
        {/* Flame Icon */}
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 grid place-items-center shadow-sm">
          <Flame size={22} className="text-white" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-900">{labels.title}</div>
          <div className="mt-1 text-sm font-semibold text-orange-600">{labels.streak}</div>
          <div className="mt-0.5 text-sm text-slate-600">{labels.bonus}</div>
          <div className="mt-0.5 text-xs text-slate-500">{labels.next}</div>
        </div>
        
        {/* Action Button */}
        <button
          onClick={onClaim}
          disabled={isClaimedToday || claiming}
          className={`flex-shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
            isClaimedToday || claiming
              ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
              : 'bg-orange-500 hover:bg-orange-600 text-white shadow-sm active:scale-[0.98]'
          }`}
          data-testid="streak-claim-btn"
        >
          {claiming ? '...' : isClaimedToday ? labels.comeback : labels.cta}
        </button>
      </div>
      
      {/* Streak Progress Dots */}
      {streakDays > 0 && streakDays <= 7 && (
        <div className="mt-3 flex items-center gap-1.5">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
            <div
              key={day}
              className={`w-3 h-3 rounded-full transition-all ${
                day <= streakDays 
                  ? 'bg-orange-500 scale-100' 
                  : 'bg-slate-200 scale-90'
              }`}
            />
          ))}
          <span className="text-xs text-slate-500 ms-2">
            {isArabic ? 'أسبوعك الأول' : 'First week'}
          </span>
        </div>
      )}
    </div>
  );
}

// =====================================================
// B) BOOST IMPACT PREVIEW
// =====================================================

export function BoostImpactPreview({
  lang = 'en',
  boostType,
  baseViews7d,
  baseCtr,
  categoryDemand,
  cityDemand,
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  
  const impact = useMemo(() => estimateBoostImpact({
    boostType,
    baseViews7d,
    baseCtr,
    categoryDemand,
    cityDemand,
  }), [boostType, baseViews7d, baseCtr, categoryDemand, cityDemand]);
  
  // Conservative display factor
  const reachPct = pct((impact.uplift - 1) * 0.85);
  const leads = impact.estLeads;
  
  const label = isArabic ? 'تقدير' : 'Estimate';
  const text = isArabic
    ? `+${reachPct} وصول • تقريباً ${leads} تفاعل`
    : `+${reachPct} reach • ~${leads} leads`;
  
  return (
    <div className="mt-2 flex items-center justify-between gap-3 px-1">
      <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
        <TrendingUp size={10} />
        {label}
      </span>
      <span className="text-xs text-slate-600">{text}</span>
    </div>
  );
}

// =====================================================
// C) BOOST SUGGESTION CARD
// =====================================================

export function BoostSuggestionCard({
  lang = 'en',
  coins = 0,
  hasPublishedAds = false,
  adTitle,
  views7d = 0,
  ctr = 0,
  messages7d = 0,
  onBoostClick,
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';
  
  // Don't show if no published ads
  if (!hasPublishedAds) return null;
  
  // Check for low performance
  const lowPerf = views7d < 80 || ctr < 0.012 || messages7d === 0;
  if (!lowPerf) return null;
  
  // Compute honest uplift cap
  const impact = estimateBoostImpact({
    boostType: 'top_category_24h',
    baseViews7d: Math.max(20, views7d),
  });
  const uplift = clamp(impact.uplift, 1.2, 3.0);
  
  const afford = coins >= 3;
  
  const labels = {
    headline: isArabic ? 'اقتراح ذكي' : 'Smart suggestion',
    title: uplift >= 2.4
      ? (isArabic 
          ? `إعلانك ممكن يجيب مشاهدات أكتر بـحوالي ${uplift.toFixed(1)}× لو عززته`
          : `This ad could get ~${uplift.toFixed(1)}× more views with a boost`)
      : (isArabic
          ? `إعلانك ممكن يجيب مشاهدات أكتر بنسبة ${round((uplift - 1) * 100)}% لو عززته`
          : `This ad could get ~${round((uplift - 1) * 100)}% more views with a boost`),
    subtitle: isArabic 
      ? 'التعزيز يساعد إعلانك يظهر للناس الصح أسرع.'
      : 'Boosting helps the right buyers see your ad faster.',
    cta: afford 
      ? (isArabic ? 'عزّز الآن' : 'Boost now') 
      : (isArabic ? 'اكسب عملات للتعزيز' : 'Earn coins to boost'),
    note: isArabic 
      ? 'تقدير — النتائج تختلف حسب المدينة والقسم.'
      : 'Estimate — results vary by city & category.',
  };
  
  return (
    <div 
      dir={dir}
      className="w-full rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50/50 p-4 shadow-sm"
      data-testid="boost-suggestion-card"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 grid place-items-center">
          <Sparkles size={18} className="text-white" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-emerald-600">{labels.headline}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{labels.title}</div>
          <div className="mt-1 text-xs text-slate-600">{labels.subtitle}</div>
          {adTitle && (
            <div className="mt-2 text-xs text-slate-500 truncate">{adTitle}</div>
          )}
          <div className="mt-2 text-[11px] text-slate-400">{labels.note}</div>
        </div>
      </div>
      
      {/* CTA Button */}
      <button
        onClick={onBoostClick}
        className={`mt-3 w-full rounded-xl py-2.5 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
          afford
            ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm active:scale-[0.98]'
            : 'bg-slate-100 text-slate-600'
        }`}
        data-testid="boost-suggestion-cta"
      >
        <Zap size={16} />
        {labels.cta}
        <ChevronRight size={16} className={isArabic ? 'rotate-180' : ''} />
      </button>
    </div>
  );
}

// =====================================================
// D) GROWTH PROGRESS BAR — Honest milestone messaging
// =====================================================

const COIN_USES = [
  { key: 'highlight', cost: 5, icon: '✨', en: 'Highlight ad (24h)', ar: 'تمييز الإعلان (24 ساعة)' },
  { key: 'top_category', cost: 15, icon: '🏅', en: 'Top of category (24h)', ar: 'أعلى الفئة (24 ساعة)' },
  { key: 'homepage', cost: 25, icon: '🏠', en: 'Homepage feature (24h)', ar: 'عرض في الصفحة الرئيسية (24 ساعة)' },
  { key: 'analytics', cost: 5, icon: '📊', en: 'Ad analytics (7 days)', ar: 'تحليلات الإعلان (7 أيام)' },
  { key: 'ai_title', cost: 2, icon: '🤖', en: 'AI title rewrite', ar: 'إعادة كتابة العنوان بالذكاء الاصطناعي' },
  { key: 'ai_desc', cost: 3, icon: '📝', en: 'AI description rewrite', ar: 'إعادة كتابة الوصف بالذكاء الاصطناعي' },
  { key: 'urgent_badge', cost: 3, icon: '🔥', en: 'Urgent badge (7 days)', ar: 'شارة عاجل (7 أيام)' },
  { key: 'extra_images', cost: 2, icon: '📸', en: 'Extra images slot', ar: 'صور إضافية' },
  { key: 'auto_refresh', cost: 10, icon: '🔄', en: 'Auto-refresh ad (24h)', ar: 'تحديث تلقائي (24 ساعة)' },
];

export function GrowthProgressBar({
  lang = 'en',
  currentCoins = 0,
  targetCoins = 50,
  onMilestoneReached,
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);
  
  const progress = Math.min(100, (currentCoins / targetCoins) * 100);
  const isUnlocked = currentCoins >= targetCoins;

  // Milestone check — fire once
  useEffect(() => {
    if (isUnlocked && onMilestoneReached) {
      onMilestoneReached();
    }
  }, [isUnlocked]);

  // Honest messaging: coins are for boosts & premium options, not a fake gate
  const hint = isUnlocked
    ? (isArabic ? 'يمكنك استخدام عملاتك في التعزيزات والأدوات المميزة' : 'Use your coins on boosts and premium tools')
    : (isArabic
      ? 'وفّر العملات لاستخدامها في التعزيزات والخيارات المميزة'
      : 'Save coins to use on boosts and premium options');

  return (
    <>
      <div dir={dir} className="w-full" data-testid="growth-progress-bar">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-semibold text-slate-800">
            {isArabic ? 'مستوى النمو' : 'Growth Level'}
          </span>
          <span className="font-bold text-green-600">
            {currentCoins} / {targetCoins} {isArabic ? 'عملة' : 'coins'}
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-green-100 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <div className="text-xs text-slate-500">{hint}</div>
          <button
            onClick={() => setShowBenefitsModal(true)}
            className="text-xs font-semibold text-green-600 hover:text-green-700 flex items-center gap-1"
            data-testid="what-unlocks-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <Info size={11} />
            {isArabic ? 'ماذا يمكنني فعله بالعملات؟' : 'What can I do with coins?'}
          </button>
        </div>
      </div>

      {showBenefitsModal && (
        <CoinBenefitsModal
          lang={lang}
          currentCoins={currentCoins}
          targetCoins={targetCoins}
          isOpen={showBenefitsModal}
          onClose={() => setShowBenefitsModal(false)}
        />
      )}
    </>
  );
}

// =====================================================
// D2) COIN BENEFITS MODAL — "What can I do with coins?"
// =====================================================

function CoinBenefitsModal({ lang, currentCoins, targetCoins, isOpen, onClose }) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';
  const isUnlocked = currentCoins >= targetCoins;
  const progress = Math.min(100, (currentCoins / targetCoins) * 100);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="coin-benefits-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        dir={dir}
        className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-5 max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <Coins size={24} style={{ color: '#F59E0B' }} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {isArabic ? 'ماذا يمكنك فعله بالعملات؟' : 'What can you do with coins?'}
            </h3>
            <p className="text-sm text-slate-500">
              {isArabic
                ? 'استخدم عملاتك لتعزيز إعلاناتك وزيادة ظهورها'
                : 'Use coins to boost your ads and increase visibility'}
            </p>
          </div>
        </div>

        {/* Current progress */}
        <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="font-semibold text-green-800">
              {isArabic ? 'رصيدك الحالي' : 'Your balance'}
            </span>
            <span className="font-bold text-green-600">
              {currentCoins} {isArabic ? 'عملة' : 'coins'}
            </span>
          </div>
          <div className="h-2 rounded-full bg-green-200 overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          {isUnlocked ? (
            <p className="text-xs text-green-700 mt-1.5 font-semibold flex items-center gap-1">
              <Check size={12} />
              {isArabic ? 'لديك رصيد كافٍ لجميع الأدوات!' : 'You have enough for all tools!'}
            </p>
          ) : (
            <p className="text-xs text-slate-500 mt-1.5">
              {isArabic
                ? `${targetCoins - currentCoins} عملة أخرى للوصول إلى هدف ${targetCoins}`
                : `${targetCoins - currentCoins} more coins to reach your ${targetCoins} coin goal`}
            </p>
          )}
        </div>

        {/* Benefits list */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            {isArabic ? 'الأدوات المتاحة' : 'Available tools'}
          </p>
          {COIN_USES.map(item => {
            const canAfford = currentCoins >= item.cost;
            return (
              <div
                key={item.key}
                className={`flex items-center gap-3 p-2.5 rounded-xl border transition-colors ${
                  canAfford
                    ? 'border-green-200 bg-green-50/50'
                    : 'border-slate-100 bg-slate-50/50'
                }`}
              >
                <span className="text-lg flex-shrink-0">{item.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">
                    {isArabic ? item.ar : item.en}
                  </div>
                </div>
                <div className={`text-xs font-bold flex-shrink-0 px-2 py-0.5 rounded-full ${
                  canAfford
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  {item.cost} {isArabic ? 'عملة' : 'coins'}
                </div>
                {canAfford && (
                  <Check size={14} className="text-green-500 flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="mt-4 w-full py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"
          data-testid="coin-benefits-close"
        >
          {isArabic ? 'حسناً' : 'Got it'}
        </button>
      </div>
    </div>
  );
}

// =====================================================
// E) HEADER UPGRADE CUE
// =====================================================

export function HeaderUpgradeCue({ lang = 'en', coins = 0 }) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  
  if (coins >= 5) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 12, fontWeight: 600, lineHeight: 1,
        color: 'rgba(255,255,255,0.9)',
      }}>
        <Sparkles size={12} />
        {isArabic ? 'جاهز لتعزيز إعلانك' : 'Ready to boost your ad'}
      </span>
    );
  }
  
  const coinsNeeded = 5 - coins;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontWeight: 600, lineHeight: 1,
      color: 'rgba(255,255,255,0.75)',
    }}>
      {isArabic 
        ? `اكسب ${coinsNeeded} عملة أخرى للتعزيز`
        : `Earn ${coinsNeeded} more ${coinsNeeded === 1 ? 'coin' : 'coins'} to boost`}
    </span>
  );
}

// =====================================================
// F) BOOST ACTIVE BADGE (Micro-urgency)
// =====================================================

export function BoostActiveBadge({ lang = 'en', hoursLeft }) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  
  if (!hoursLeft || hoursLeft <= 0) return null;
  
  const label = isArabic 
    ? `التعزيز نشط • ${hoursLeft} ساعة متبقية`
    : `Boost active • ${hoursLeft}h left`;
  
  return (
    <div className="mt-2 text-center">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
        <Zap size={12} />
        {label}
      </span>
    </div>
  );
}

// =====================================================
// G) UNIFIED WAYS TO EARN - All earning methods in one section
// =====================================================

const STATUS_BADGES = {
  active: { en: 'Active', ar: 'نشط', color: 'bg-emerald-100 text-emerald-700' },
  auto: { en: 'Auto', ar: 'تلقائي', color: 'bg-blue-100 text-blue-700' },
  coming_soon: { en: 'Coming soon', ar: 'قريباً', color: 'bg-slate-100 text-slate-500' },
};

const EARN_ICONS = {
  percent: '💰',
  trophy: '🏆',
  'share-2': '📤',
  zap: '⚡',
};

/**
 * Unified Ways to Earn component - single section with all earning methods
 * Order: Post Ad, Invite Friends, Cashback, Volume, Sharing, Fast Responder
 */
export function UnifiedWaysToEarn({
  lang = 'en',
  // Basic actions
  onPostAd,
  onInvite,
  // Advanced items from backend
  waysToEarn = [],
  loading = false,
  onAdvancedItemClick,
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';

  // Basic earning items (static)
  const basicItems = [
    {
      key: 'post_ad',
      icon: '🟩',
      title: isArabic ? 'نشر إعلان جديد' : 'Post a New Ad',
      desc: isArabic ? 'اكسب عملات عند نشر إعلان' : 'Earn coins when you post an ad',
      badge: null,
      progress: null,
      onClick: onPostAd,
      type: 'basic',
    },
    {
      key: 'invite_friends',
      icon: '🤝',
      title: isArabic ? 'دعوة الأصدقاء' : 'Invite Friends',
      desc: isArabic ? 'اكسب عملات عند تسجيل صديق' : 'Earn coins when a friend signs up',
      badge: null,
      progress: null,
      onClick: onInvite,
      type: 'basic',
    },
  ];

  // Convert advanced items to consistent format
  const advancedItems = waysToEarn.map(item => ({
    key: item.key,
    icon: EARN_ICONS[item.icon] || '🪙',
    title: isArabic ? item.name_ar : item.name_en,
    desc: isArabic ? item.desc_ar : item.desc_en,
    badge: item.badge,
    status: item.status,
    progress: item.progress,
    stats: item.stats,
    onClick: () => onAdvancedItemClick?.(item),
    type: 'advanced',
  }));

  // Combine all items
  const allItems = [...basicItems, ...advancedItems];

  // Loading skeleton
  if (loading) {
    return (
      <section dir={dir} className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="unified-ways-to-earn">
        <div className="animate-pulse">
          <div className="h-5 bg-slate-200 rounded w-32 mb-4"></div>
          <div className="space-y-4">
            {[1,2,3,4,5,6,7].map(i => (
              <div key={i} className="h-14 bg-slate-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section dir={dir} className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="unified-ways-to-earn">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900">
          {isArabic ? 'طرق كسب العملات' : 'Ways to Earn'}
        </h3>
        <span className="text-xs text-slate-500">
          {isArabic ? 'اكسب المزيد' : 'Earn more'}
        </span>
      </div>

      {/* Rows - consistent 16px gap */}
      <div className="flex flex-col gap-4">
        {allItems.map((item) => {
          const badgeInfo = item.badge 
            ? (STATUS_BADGES[item.badge.toLowerCase()] || STATUS_BADGES[item.status] || null)
            : null;

          return (
            <button
              key={item.key}
              onClick={item.onClick}
              className={`w-full rounded-xl border border-slate-100 bg-slate-50 hover:bg-slate-100 active:scale-[0.99] transition-all flex items-center justify-between ${isArabic ? 'text-right' : 'text-left'}`}
              style={{ paddingInlineEnd: '16px', paddingInlineStart: '12px', paddingTop: '12px', paddingBottom: '12px' }}
              data-testid={`earn-row-${item.key}`}
            >
              {/* Left side: Icon + Content */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">{item.icon}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">
                      {item.title}
                    </span>
                    {badgeInfo && (
                      <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${badgeInfo.color}`}>
                        {isArabic ? badgeInfo.ar : badgeInfo.en}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 truncate">
                    {item.desc}
                  </div>
                  {item.progress && (
                    <div className="text-[10px] text-orange-600 font-medium mt-0.5">
                      {item.progress}
                    </div>
                  )}
                </div>
              </div>

              {/* Right side: Arrow - fixed size container for perfect centering */}
              <div 
                className="flex-shrink-0 flex items-center justify-center"
                style={{ width: '24px', height: '24px' }}
              >
                <svg
                  className={`h-4 w-4 text-slate-400 ${isArabic ? 'rotate-180' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// Legacy component - kept for backward compatibility but now just wraps UnifiedWaysToEarn
export function WaysToEarnCoins({
  lang = 'en',
  onPostAd,
  onInvite,
}) {
  return (
    <UnifiedWaysToEarn
      lang={lang}
      onPostAd={onPostAd}
      onInvite={onInvite}
      waysToEarn={[]}
      loading={false}
    />
  );
}

// Legacy AdvancedWaysToEarn - now deprecated, use UnifiedWaysToEarn instead
export function AdvancedWaysToEarn({
  lang = 'en',
  waysToEarn = [],
  loading = false,
  onItemClick,
}) {
  // This component is deprecated - the parent should use UnifiedWaysToEarn instead
  // Keeping for backward compatibility but returning null
  return null;
}

// =====================================================
// H) EARNING INFO MODAL
// =====================================================

export function EarningInfoModal({
  lang = 'en',
  isOpen,
  onClose,
  earningType,
  stats = {},
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';

  if (!isOpen || !earningType) return null;

  const modals = {
    cashback_transaction: {
      title_en: 'Coins Back on Completed Deals',
      title_ar: 'استرداد عملات عند إتمام الصفقة',
      content_en: [
        'Earn coins back when a deal is completed',
        `Maximum ${stats.cap_per_tx || 20} coins per transaction`,
        `Daily limit: ${stats.daily_cap || 50} coins`,
        'Coins are credited automatically after completion is confirmed',
      ],
      content_ar: [
        'اربح عملات مسترجعة عند إتمام الصفقة',
        `الحد الأقصى ${stats.cap_per_tx || 20} عملة لكل معاملة`,
        `الحد اليومي: ${stats.daily_cap || 50} عملة`,
        'تُضاف العملات تلقائيًا بعد تأكيد إتمام الصفقة',
      ],
      icon: '💰',
    },
    seller_volume_bonus: {
      title_en: 'Seller Volume Milestones',
      title_ar: 'إنجازات حجم المبيعات',
      content_en: [
        'Earn bonus coins when you reach sales milestones:',
        '• 5 sales → +20 coins',
        '• 10 sales → +50 coins',
        '• 20 sales → +120 coins',
        '• 50 sales → +300 coins',
        '• 100 sales → +700 coins',
        'Only completed sales count. Each milestone can be claimed once.',
      ],
      content_ar: [
        'اكسب عملات إضافية عند تحقيق أهداف المبيعات:',
        '• 5 مبيعات ← +20 عملة',
        '• 10 مبيعات ← +50 عملة',
        '• 20 مبيعات ← +120 عملة',
        '• 50 مبيعات ← +300 عملة',
        '• 100 مبيعات ← +700 عملة',
        'تُحسب المبيعات المكتملة فقط. كل إنجاز يُطالب به مرة واحدة.',
      ],
      icon: '🏆',
    },
    social_sharing: {
      title_en: 'Social Sharing Rewards',
      title_ar: 'مكافآت المشاركة الاجتماعية',
      content_en: [
        `Earn ${stats.reward_per_share || 2} coins every time you share`,
        `Daily limit: ${stats.daily_cap || 10} coins`,
        'Share your listings on WhatsApp, Facebook, Twitter, or copy link',
        'Each unique item can be shared once per day for coins',
      ],
      content_ar: [
        `اكسب ${stats.reward_per_share || 2} عملة كل ما تشارك`,
        `الحد اليومي: ${stats.daily_cap || 10} عملات`,
        'شارك إعلاناتك على واتساب، فيسبوك، تويتر، أو انسخ الرابط',
        'كل عنصر يُمكن مشاركته مرة واحدة يومياً للحصول على عملات',
      ],
      icon: '📤',
    },
    fast_responder: {
      title_en: 'Fast Responder Bonus',
      title_ar: 'مكافأة الرد السريع',
      content_en: [
        `Reply within ${stats.time_limit_minutes || 30} minutes to earn coins`,
        `+${stats.reward_per_response || 1} coin per fast reply`,
        `Daily limit: ${stats.daily_cap || 10} coins`,
        'Only counts for new message threads (first buyer message)',
        stats.response_rate_7d !== null 
          ? `Your 7-day response rate: ${stats.response_rate_7d}%` 
          : 'Start responding quickly to build your rate!',
      ],
      content_ar: [
        `رد خلال ${stats.time_limit_minutes || 30} دقيقة لتكسب عملات`,
        `+${stats.reward_per_response || 1} عملة لكل رد سريع`,
        `الحد اليومي: ${stats.daily_cap || 10} عملات`,
        'يُحسب فقط للرسائل الجديدة (أول رسالة من المشتري)',
        stats.response_rate_7d !== null 
          ? `معدل ردك خلال 7 أيام: ${stats.response_rate_7d}%`
          : 'ابدأ الرد بسرعة لبناء معدلك!',
      ],
      icon: '⚡',
    },
  };

  const modal = modals[earningType] || modals.cashback_transaction;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        dir={dir}
        className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-5 animate-in fade-in zoom-in-95 duration-200"
        data-testid="earning-info-modal"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-xl bg-orange-100 flex items-center justify-center">
            <span className="text-2xl">{modal.icon}</span>
          </div>
          <h3 className="text-lg font-bold text-slate-900">
            {isArabic ? modal.title_ar : modal.title_en}
          </h3>
        </div>
        
        {/* Content */}
        <div className="space-y-2 mb-5">
          {(isArabic ? modal.content_ar : modal.content_en).map((line, idx) => (
            <p key={idx} className="text-sm text-slate-600">
              {line}
            </p>
          ))}
        </div>
        
        {/* Today's Stats */}
        {(stats.today_earned !== undefined || stats.remaining_today !== undefined) && (
          <div className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-100">
            <div className="text-xs text-slate-500 mb-1">
              {isArabic ? 'إحصائيات اليوم' : "Today's Stats"}
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-700">
                {isArabic ? 'مكتسب:' : 'Earned:'}
              </span>
              <span className="font-semibold text-orange-600">
                {stats.today_earned || 0} {isArabic ? 'عملة' : 'coins'}
              </span>
            </div>
            {stats.remaining_today !== undefined && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-700">
                  {isArabic ? 'متبقي:' : 'Remaining:'}
                </span>
                <span className="font-medium text-slate-600">
                  {stats.remaining_today} {isArabic ? 'عملة' : 'coins'}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 active:scale-[0.98] transition-all"
        >
          {isArabic ? 'فهمت' : 'Got it'}
        </button>
      </div>
    </div>
  );
}

// =====================================================
// J) MILESTONES MODAL
// =====================================================

export function MilestonesModal({
  lang = 'en',
  isOpen,
  onClose,
  milestones = [],
  completedSales = 0,
  onClaimMilestone,
  claiming = false,
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      <div 
        dir={dir}
        className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-5 max-h-[80vh] overflow-y-auto"
        data-testid="milestones-modal"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
            <span className="text-2xl">🏆</span>
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {isArabic ? 'إنجازات المبيعات' : 'Sales Milestones'}
            </h3>
            <p className="text-sm text-slate-500">
              {isArabic 
                ? `${completedSales} عملية بيع مكتملة`
                : `${completedSales} completed sales`}
            </p>
          </div>
        </div>
        
        <div className="space-y-3">
          {milestones.map((ms, idx) => {
            const progress = Math.min(100, (completedSales / ms.sales) * 100);
            const isAchieved = completedSales >= ms.sales;
            const isClaimed = ms.claimed;
            const canClaim = isAchieved && !isClaimed;
            
            return (
              <div 
                key={idx}
                className={`p-3 rounded-xl border ${
                  isClaimed 
                    ? 'border-emerald-200 bg-emerald-50' 
                    : isAchieved 
                      ? 'border-orange-200 bg-orange-50' 
                      : 'border-slate-200 bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-900">
                    {ms.sales} {isArabic ? 'مبيعات' : 'sales'}
                  </span>
                  <span className={`font-bold ${isClaimed ? 'text-emerald-600' : 'text-orange-600'}`}>
                    +{ms.reward} {isArabic ? 'عملة' : 'coins'}
                  </span>
                </div>
                
                {/* Progress bar */}
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                  <div 
                    className={`h-full transition-all ${
                      isClaimed ? 'bg-emerald-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {completedSales}/{ms.sales}
                  </span>
                  
                  {isClaimed ? (
                    <span className="text-xs font-medium text-emerald-600">
                      ✓ {isArabic ? 'تم المطالبة' : 'Claimed'}
                    </span>
                  ) : canClaim ? (
                    <button
                      onClick={() => onClaimMilestone?.(`volume_${ms.sales}`)}
                      disabled={claiming}
                      className="px-3 py-1 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 active:scale-[0.98] disabled:opacity-50"
                    >
                      {claiming ? '...' : (isArabic ? 'اطلب المكافأة' : 'Claim')}
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">
                      {isArabic ? 'غير مؤهل' : 'Locked'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <button
          onClick={onClose}
          className="w-full mt-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200 active:scale-[0.98] transition-all"
        >
          {isArabic ? 'إغلاق' : 'Close'}
        </button>
      </div>
    </div>
  );
}

// =====================================================
// J) SHARE SHEET MODAL - Fixed with working share buttons
// =====================================================

export function ShareSheetModal({
  lang = 'en',
  isOpen,
  onClose,
  shareableItems = [],
  onShare,
  sharing = false,
  stats = {},
  onPostAd,
  userId = '',
}) {
  const isArabic = lang?.toLowerCase?.().startsWith('ar');
  const dir = isArabic ? 'rtl' : 'ltr';
  const [selectedItem, setSelectedItem] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedItem(null);
      setLinkCopied(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Generate share link for an item — always on the public domain
  const getShareLink = (item) => {
    const itemId = item?.ad_id || item?.id || 'listing';
    const refParam = userId ? `?ref=${userId}` : '';
    return buildShareUrl(`/ad/${itemId}${refParam}`);
  };

  // Get share message
  const getShareMessage = (item) => {
    const title = item?.title || (isArabic ? 'شاهد هذا الإعلان' : 'Check out this listing');
    const link = getShareLink(item);
    return `${title}\n${link}`;
  };

  // Share handlers for each platform
  const handleWhatsAppShare = async (item) => {
    const message = encodeURIComponent(getShareMessage(item));
    window.open(`https://wa.me/?text=${message}`, '_blank');
    if (onShare) await onShare(item, 'whatsapp');
  };

  const handleMessengerShare = async (item) => {
    const link = encodeURIComponent(getShareLink(item));
    window.open(`https://www.facebook.com/dialog/send?link=${link}&app_id=966242223397117&redirect_uri=${encodeURIComponent(window.location.href)}`, '_blank');
    if (onShare) await onShare(item, 'messenger');
  };

  const handleGmailShare = async (item) => {
    const subject = encodeURIComponent(isArabic ? 'إعلان من وصلة' : 'Listing from Wassla');
    const body = encodeURIComponent(getShareMessage(item));
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    if (onShare) await onShare(item, 'gmail');
  };

  const handleCopyLink = async (item) => {
    const link = getShareLink(item);
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      if (onShare) await onShare(item, 'copy_link');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async (item) => {
    const res = await shareNative({
      title: item?.title || 'Wassla Listing',
      text: isArabic ? 'شاهد هذا الإعلان على وصلة' : 'Check out this listing on Wassla',
      url: getShareLink(item),
    });
    if (res.completed) {
      if (res.method !== 'canceled' && onShare) await onShare(item, 'native');
      return;
    }
    // Fallback to copy
    handleCopyLink(item);
  };

  // Platform buttons config (removed Twitter)
  const platforms = [
    { 
      id: 'whatsapp', 
      name: 'WhatsApp', 
      icon: '💬', 
      color: 'bg-green-500',
      handler: handleWhatsAppShare,
    },
    { 
      id: 'messenger', 
      name: 'Messenger', 
      icon: '💭', 
      color: 'bg-blue-500',
      handler: handleMessengerShare,
    },
    { 
      id: 'gmail', 
      name: 'Gmail', 
      icon: '📧', 
      color: 'bg-red-500',
      handler: handleGmailShare,
    },
    { 
      id: 'copy_link', 
      name: linkCopied ? (isArabic ? 'تم النسخ!' : 'Copied!') : (isArabic ? 'نسخ' : 'Copy'), 
      icon: linkCopied ? '✓' : '🔗', 
      color: linkCopied ? 'bg-emerald-500' : 'bg-slate-600',
      handler: handleCopyLink,
    },
  ];

  // Check if native share is available (Capacitor native or Web Share API)
  const hasNativeShare = canNativeShare();

  // Current item to share
  const itemToShare = selectedItem || shareableItems[0];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      <div 
        dir={dir}
        className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md p-5 animate-in slide-in-from-bottom sm:fade-in sm:zoom-in-95 duration-200"
        data-testid="share-sheet-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">
            {isArabic ? 'شارك واكسب' : 'Share & Earn'}
          </h3>
          <span className="text-sm text-orange-600 font-medium">
            +{stats.reward_per_share || 2} {isArabic ? 'عملة/مشاركة' : 'coins/share'}
          </span>
        </div>
        
        {/* Remaining today */}
        <div className="mb-4 p-2 rounded-lg bg-orange-50 text-center">
          <span className="text-sm text-orange-700">
            {isArabic 
              ? `${stats.shares_remaining ?? 5} مشاركات متبقية اليوم`
              : `${stats.shares_remaining ?? 5} shares remaining today`}
          </span>
        </div>
        
        {/* Shareable items or Post Ad CTA */}
        {shareableItems.length > 0 ? (
          <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
            <p className="text-xs text-slate-500 mb-2">
              {isArabic ? 'اختر عنصراً للمشاركة:' : 'Choose an item to share:'}
            </p>
            {shareableItems.map((item, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedItem(item)}
                disabled={sharing}
                className={`w-full p-2 rounded-lg border text-sm flex items-center gap-2 transition-all ${
                  selectedItem?.ad_id === item.ad_id || (selectedItem === null && idx === 0)
                    ? 'border-orange-300 bg-orange-50'
                    : 'border-slate-200 hover:bg-slate-50'
                } ${isArabic ? 'text-right' : 'text-left'}`}
              >
                <span className="text-slate-400">📄</span>
                <span className="truncate flex-1">{item.title || item.name}</span>
                {(selectedItem?.ad_id === item.ad_id || (selectedItem === null && idx === 0)) && (
                  <span className="text-orange-500">✓</span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="mb-4 p-4 rounded-xl bg-slate-50 text-center">
            <p className="text-sm text-slate-500 mb-3">
              {isArabic 
                ? 'انشر إعلاناً أولاً لتتمكن من المشاركة والكسب!'
                : 'Post an ad first to share and earn!'}
            </p>
            {onPostAd && (
              <button
                onClick={onPostAd}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-colors"
              >
                {isArabic ? 'نشر إعلان' : 'Post an Ad'}
              </button>
            )}
          </div>
        )}
        
        {/* Platform buttons */}
        {shareableItems.length > 0 && (
          <>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {platforms.map(p => (
                <button
                  key={p.id}
                  onClick={() => p.handler(itemToShare)}
                  disabled={sharing || shareableItems.length === 0}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-all active:scale-95"
                >
                  <div className={`h-10 w-10 rounded-full ${p.color} flex items-center justify-center text-white`}>
                    <span className="text-lg">{p.icon}</span>
                  </div>
                  <span className="text-[10px] text-slate-600 font-medium">{p.name}</span>
                </button>
              ))}
            </div>

            {/* Native share button if available */}
            {hasNativeShare && (
              <button
                onClick={() => handleNativeShare(itemToShare)}
                disabled={sharing}
                className="w-full py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 flex items-center justify-center gap-2 mb-3"
              >
                <span>📲</span>
                {isArabic ? 'المزيد من خيارات المشاركة' : 'More sharing options'}
              </button>
            )}
          </>
        )}
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200"
        >
          {isArabic ? 'إغلاق' : 'Close'}
        </button>
      </div>
    </div>
  );
}

export default {
  StreakCard,
  BoostImpactPreview,
  BoostSuggestionCard,
  GrowthProgressBar,
  HeaderUpgradeCue,
  BoostActiveBadge,
  WaysToEarnCoins,
  AdvancedWaysToEarn,
  UnifiedWaysToEarn,
  EarningInfoModal,
  MilestonesModal,
  ShareSheetModal,
  estimateBoostImpact,
  calculateStreakBonus,
  daysUntilNextBonus,
};
